import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useBrand } from "@/context/BrandContext";
import { LockKeyhole } from "lucide-react";
import { InviteStatePanel } from "@/components/auth/InviteStatePanel";

export default function ResetPassword() {
  const { brand, logoSrc } = useBrand();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(token ? { status: "checking" } : { status: "invalid" });

  const check = useCallback(() => {
    setState({ status: "checking" });
    api.get("/auth/reset-token-status", { params: { token } }).then((r) => setState(r.data)).catch(() => setState({ status: "error" }));
  }, [token]);
  useEffect(() => { if (token) check(); }, [token, check]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success(state.kind === "invite" ? "Account ready — sign in with your new password" : "Password updated — sign in with your new password");
      nav("/login");
    } catch (err) {
      const d = err?.response?.data?.detail || "";
      if (/expired/i.test(d)) return setState({ status: "expired" });
      if (/already used/i.test(d)) return setState({ status: "used" });
      toast.error(apiError(err, "Reset failed"));
    } finally { setLoading(false); }
  };

  if (state.status === "checking") return <div className="min-h-screen bg-[#0b0f17] flex items-center justify-center text-slate-400 text-sm" data-testid="reset-checking">Verifying your link…</div>;
  if (state.status !== "valid") return <InviteStatePanel status={state.status} onRetry={check} testId="reset-state" />;
  const isInvite = state.kind === "invite";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0f17] grid-bg p-6">
      <form onSubmit={submit} className="w-full max-w-md sc-card p-8" data-testid="reset-password-form">
        <div className="flex items-center gap-3 mb-6">
          <img src={logoSrc} alt={brand.product_name} className="w-14 h-14 object-contain mix-blend-screen" />
          <div>
            <div className="font-heading text-lg font-extrabold"><span className="text-slate-100">STEEL</span><span className="text-amber-400">FORGE AI</span></div>
            <div className="label-cap">{isInvite ? "Welcome — set your password" : "Choose a new password"}</div>
          </div>
        </div>
        <label className="block mb-4">
          <span className="label-cap">New password (min 8 chars)</span>
          <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
            className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 focus:outline-none focus:border-amber-500" data-testid="reset-password-input" />
        </label>
        <label className="block mb-5">
          <span className="label-cap">Confirm password</span>
          <input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)}
            className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 focus:outline-none focus:border-amber-500" data-testid="reset-password-confirm-input" />
        </label>
        <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2.5 rounded-md transition disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="reset-submit-button">
          <LockKeyhole className="w-4 h-4" /> {loading ? "Updating…" : "Set new password"}
        </button>
        <div className="mt-5 text-sm text-slate-400 text-center">
          <Link to="/forgot-password" className="text-amber-400 hover:text-amber-300" data-testid="reset-request-new-link">Request a new link</Link>
        </div>
      </form>
    </div>
  );
}
